"use client";

import { useState, useEffect, useMemo } from 'react';
import { Box, Button, Grid, TextField, Typography, Paper, CircularProgress, Snackbar, Alert, MenuItem, InputAdornment, Select, ListSubheader } from '@mui/material';

import { sendContactForm } from '@/lib/contact';
import { saveToSheet } from '@/lib/googleSheet';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { countryCodes } from '@/data/contact';

const ContactForm = () => {
    const t = useTranslations('ContactPage.ContactForm');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        companyName: '',
        roomCount: '',
        phoneNumber: '',
        source: '',
        message: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [phonePrefix, setPhonePrefix] = useState('+81');
    const [searchPrefix, setSearchPrefix] = useState('');

    const displayedCountries = useMemo(() => {
        const term = searchPrefix.toLowerCase();
        return countryCodes.filter(c =>
            c.label.toLowerCase().includes(term) ||
            c.value.toLowerCase().includes(term) ||
            (c.name && c.name.toLowerCase().includes(term))
        );
    }, [searchPrefix]);

    const MAX_SUBMISSIONS = 3;
    const TIME_WINDOW = 5 * 60 * 1000; // 5 minutes
    const LOCKOUT_DURATION = 60 * 60 * 1000; // 1 hour

    const router = useRouter();

    useEffect(() => {
        checkRateLimit();
    }, []);

    const checkRateLimit = () => {
        const lockoutUntil = localStorage.getItem('contact_form_lockout');
        if (lockoutUntil && Date.now() < parseInt(lockoutUntil, 10)) {
            setIsRateLimited(true);
            return true;
        } else if (lockoutUntil) {
            // Lockout expired
            localStorage.removeItem('contact_form_lockout');
            localStorage.removeItem('contact_form_submissions');
            setIsRateLimited(false);
        }
        return false;
    };

    const registerSubmission = () => {
        const now = Date.now();
        const storedSubmissions = JSON.parse(localStorage.getItem('contact_form_submissions') || '[]');

        // Filter out old submissions outside the window
        const validSubmissions = storedSubmissions.filter((timestamp: number) => now - timestamp < TIME_WINDOW);

        validSubmissions.push(now);
        localStorage.setItem('contact_form_submissions', JSON.stringify(validSubmissions));

        if (validSubmissions.length >= MAX_SUBMISSIONS) {
            const lockoutTime = now + LOCKOUT_DURATION;
            localStorage.setItem('contact_form_lockout', lockoutTime.toString());
            setIsRateLimited(true);
        }
    };

    const sourceOptions = [
        {
            label: t('source_opt_1'),
            value: 'インターネット検索'
        }, {
            label: t('source_opt_2'),
            value: 'ソーシャルメディア'
        }, {
            label: t('source_opt_3'),
            value: '友人や同僚からの紹介'
        }, {
            label: t('source_opt_4'),
            value: '民泊関連サービスからの紹介'
        }, {
            label: t('source_opt_5'),
            value: '広告'
        },
        {
            label: t('source_opt_6'),
            value: '業界イベント'
        }, {
            label: t('source_opt_7'),
            value: 'その他'
        }
    ];

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) newErrors.name = t('name_error');

        if (!formData.email.trim()) {
            newErrors.email = t('email_error_req');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('email_error_invalid');
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = t('phone_error');
        } else if (!/^[0-9]{5,15}$/.test(formData.phoneNumber.trim())) {
            newErrors.phoneNumber = t('phone_error_invalid');
        }

        if (!formData.source) newErrors.source = t('source_error');
        if (!formData.message.trim()) newErrors.message = t('message_error');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        let newValue = value;
        if (name === 'phoneNumber') {
            // Strip any non-digit characters
            newValue = value.replace(/[^\d]/g, '');
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));

        // Validation on change
        if (name === 'phoneNumber') {
            const val = newValue.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error') }));
            } else if (!/^[0-9]{5,15}$/.test(val)) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error_invalid') }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.phoneNumber;
                    return newErrors;
                });
            }
        } else if (name === 'email') {
            const val = newValue.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, email: t('email_error_req') }));
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setErrors(prev => ({ ...prev, email: t('email_error_invalid') }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.email;
                    return newErrors;
                });
            }
        } else {
            // Clear error when user starts typing for other fields
            if (errors[name]) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'phoneNumber') {
            const val = value.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error') }));
            } else if (!/^[0-9]{5,15}$/.test(val)) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error_invalid') }));
            }
        } else if (name === 'email') {
            const val = value.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, email: t('email_error_req') }));
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setErrors(prev => ({ ...prev, email: t('email_error_invalid') }));
            }
        }
    };

    const handleSubmit = async () => {
        if (isRateLimited || checkRateLimit()) return;
        if (!validate()) return;

        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 3000));

            const submissionData = {
                ...formData,
                phoneNumber: `${phonePrefix}-${formData.phoneNumber}`
            };

            const response = await sendContactForm(submissionData);
            await saveToSheet(submissionData);

            if (response.success) {
                setSuccess(true);
                setError(false);
                registerSubmission();

                setFormData({
                    name: '',
                    email: '',
                    companyName: '',
                    roomCount: '',
                    phoneNumber: '',
                    source: '',
                    message: ''
                });
            }
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (success) {
            router.push('/contact/thanks');
        }
    }, [success, router]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
        >
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
                <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    {t('success_msg')}
                </Alert>
            </Snackbar>

            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={error} autoHideDuration={3000} onClose={() => setError(false)}>
                <Alert onClose={() => setError(false)} severity="error" sx={{ width: '100%' }}>
                    {t('error_msg')}
                </Alert>
            </Snackbar>

            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
                {t('title')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', mb: 4 }}>
                {t('description')}
            </Typography>

            <Grid container spacing={4}>
                {/* Name */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('name_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('name_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder={t('name_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Email */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('email_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('email_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.email}
                        helperText={errors.email}
                        placeholder={t('email_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Company Name */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('company_label')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder={t('company_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Room Count */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('room_count_label')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="roomCount"
                        value={formData.roomCount}
                        onChange={handleChange}
                        placeholder={t('room_count_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Phone Number */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('phone_label')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField
                        fullWidth
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber}
                        placeholder={t('phone_placeholder')}
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Select
                                            variant="standard"
                                            disableUnderline
                                            value={phonePrefix}
                                            onChange={(e) => setPhonePrefix(e.target.value)}
                                            onClose={() => setSearchPrefix('')}
                                            renderValue={(selected) => {
                                                const country = countryCodes.find(c => c.value === selected);
                                                return country ? `${country.flag} ${country.label}` : selected as string;
                                            }}
                                            MenuProps={{
                                                autoFocus: false,
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                        width: 250,
                                                    },
                                                },
                                            }}
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '1rem',
                                                minWidth: '80px',
                                                '& .MuiSelect-select': { py: 0, pl: 0, pr: 2 },
                                            }}
                                        >
                                            <ListSubheader sx={{ pt: 1, pb: 1, zIndex: 2 }}>
                                                <TextField
                                                    size="small"
                                                    autoFocus
                                                    placeholder="Search prefix or country"
                                                    fullWidth
                                                    value={searchPrefix}
                                                    onChange={(e) => setSearchPrefix(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key !== 'Escape') {
                                                            e.stopPropagation();
                                                        }
                                                    }}
                                                />
                                            </ListSubheader>
                                            {displayedCountries.length === 0 ? (
                                                <MenuItem disabled>No results found</MenuItem>
                                            ) : (
                                                displayedCountries.map((country) => (
                                                    <MenuItem key={country.value} value={country.value}>
                                                        {country.flag} {country.name} ({country.label})
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </InputAdornment>
                                ),
                            }
                        }}
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* How did you know about us? */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('source_label')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField
                        fullWidth
                        select
                        name="source"
                        value={formData.source}
                        onChange={handleChange}
                        error={!!errors.source}
                        helperText={errors.source}
                        SelectProps={{
                            displayEmpty: true,
                            renderValue: (selected) => {
                                if ((selected as string).length === 0) {
                                    return <Typography color="text.secondary">{t('source_select_placeholder')}</Typography>;
                                }
                                return sourceOptions.find(opt => opt.value === selected)?.label || (selected as string);
                            }
                        }}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    >
                        {sourceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Message */}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('message_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('message_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        error={!!errors.message}
                        helperText={errors.message}
                        placeholder={t('message_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Button
                        id='contact_button_submit'
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={loading || isRateLimited}
                        sx={{
                            backgroundColor: isRateLimited ? '#94a3b8' : '#F15859',
                            fontWeight: 'bold',
                            py: 2,
                            fontSize: '1rem',
                            borderRadius: '8px',
                            boxShadow: isRateLimited ? 'none' : '0 4px 6px -1px rgba(225, 29, 72, 0.3)',
                            '&:hover': {
                                backgroundColor: isRateLimited ? '#94a3b8' : '#be123c'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : isRateLimited ? t('submit_btn_rate_limited') : t('submit_btn')}
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#64748b' }}>
                        {t('submit_notice')}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ContactForm;
